import { Link, useNavigate, useLocation } from "react-router-dom";
import { useMemo, useState } from "react";
import sulsaLogo from "../../../assets/sulsa-logo.png";
import {
  LayoutDashboard,
  Receipt,
  Package,
  Users,
  UserCog,
  ShieldCheck,
  Settings,
  LogOut,
  Store as StoreIcon,
  Menu,
  X,
  ScrollText,
  Landmark,
  PackageOpen,
  ClipboardEdit,
  BellRing,
  PackageCheck,
  BookText,
  ReceiptText,
  Truck,
  Building2,
  Wallet,
  RotateCcw,
  Award,
  Gift,
  Percent,
  ArrowLeftRight,
  ChevronDown,
  Database,
  Warehouse,
  BarChart3,
  Calculator,
  SlidersHorizontal,
} from "lucide-react";
import { useAuth } from "../../../lib/auth";
import NotificationBell from "./NotificationBell";
import { Popconfirm } from "antd";
import AppSelect from "../../../components/ui/AppSelect";
import Notification from "../../../helpers/Notification";
import InitialsAvatar from "../ui/InitialsAvatar";
import { actionName, actionUrl, moduleName } from "../../../constants/defaultValues";
import { hasAccessByOptions } from "../../../helpers/helper";
import Request from "../../../helpers/request.service";
import Auth from "../../../helpers/auth.service";
import { authUrl } from "../../../constants/apiBaseUrl";

/**
 * Build a sidebar item only if its permission gate passes.
 * Mirrors DocAI's getItem() pattern.
 */
function navItem(to, label, Icon, options = {}, opts = {}) {
  const { highlight = false } = opts;
  if (!hasAccessByOptions(options)) return null;
  return { to, label, icon: Icon, highlight };
}

/**
 * Parent nav group with indented child links (e.g. Transfer → Challan /
 * Invoice). Hidden entirely when no child passes its permission gate.
 */
function navGroup(label, Icon, children) {
  const visible = children.filter(Boolean);
  if (!visible.length) return null;
  return { label, icon: Icon, children: visible };
}

const NAV_DEFS = () =>
  [
    navItem("/", "Dashboard", LayoutDashboard, { isAlwaysAllowed: true }),
    navItem(
      "/pos",
      "POS / Billing",
      Receipt,
      {
        mandatoryPermissions: [
          { module: moduleName.POS, method: actionName.GET, actionUrl: actionUrl.POS_VIEW },
        ],
      },
      { highlight: true },
    ),
    navItem("/pos/history", "Sales History", ReceiptText, {
      mandatoryPermissions: [
        { module: moduleName.POS, method: actionName.GET, actionUrl: actionUrl.POS_VIEW },
      ],
    }),
    navGroup("Inventory Mgmt", Warehouse, [
      navItem("/inventory/products", "Products", Package, {
        mandatoryPermissions: [
          {
            module: moduleName.PRODUCTS,
            method: actionName.GET,
            actionUrl: actionUrl.PRODUCT_LIST,
          },
        ],
      }),
      navItem("/inventory/inbound-parents", "Inventory", PackageOpen, {
        mandatoryPermissions: [
          {
            module: moduleName.PRODUCTS,
            method: actionName.GET,
            actionUrl: actionUrl.INBOUND_PARENT_LIST,
          },
        ],
      }),
      navItem("/grn", "Goods Receipt", PackageCheck, {
        mandatoryPermissions: [
          { module: moduleName.GRN, method: actionName.GET, actionUrl: actionUrl.GRN_LIST },
        ],
      }),
    ]),
    navItem("/customers", "Customers", Users, {
      mandatoryPermissions: [
        { module: moduleName.CUSTOMER, method: actionName.GET, actionUrl: actionUrl.CUSTOMER_LIST },
      ],
    }),
    navItem("/credit-notes", "Credit Notes", Wallet, {
      mandatoryPermissions: [
        {
          module: moduleName.CREDIT_NOTE,
          method: actionName.GET,
          actionUrl: actionUrl.CREDIT_NOTE_LIST,
        },
      ],
    }),
    navItem("/returns", "Returns", RotateCcw, {
      mandatoryPermissions: [
        {
          module: moduleName.RETURNS,
          method: actionName.GET,
          actionUrl: actionUrl.RETURNABLE_BILL,
        },
      ],
    }),
    navGroup("Transfer", ArrowLeftRight, [
      navItem("/multi-challan", "Challan", ClipboardEdit, {
        mandatoryPermissions: [
          {
            module: moduleName.WHOLESALE,
            method: actionName.GET,
            actionUrl: actionUrl.CHALLAN_LIST,
          },
        ],
      }),
      navItem("/multi-invoice", "Invoice", Receipt, {
        mandatoryPermissions: [
          {
            module: moduleName.WHOLESALE,
            method: actionName.GET,
            actionUrl: actionUrl.INVOICE_LIST,
          },
        ],
      }),
    ]),
    navGroup("Master", Database, [
      navItem("/users", "Users", UserCog, {
        mandatoryPermissions: [
          { module: moduleName.USER, method: actionName.GET, actionUrl: actionUrl.USER_LIST },
        ],
      }),
      navItem("/roles", "Roles", ShieldCheck, {
        mandatoryPermissions: [
          { module: moduleName.ROLE, method: actionName.GET, actionUrl: actionUrl.ROLE_LIST },
        ],
      }),
      navItem("/stores", "Stores", StoreIcon, {
        mandatoryPermissions: [
          { module: moduleName.STORE, method: actionName.GET, actionUrl: actionUrl.STORE_LIST },
        ],
      }),
      navItem("/store-type-access", "Store Type Access", Building2, {
        mandatoryPermissions: [
          {
            module: moduleName.ROLE,
            method: actionName.GET,
            actionUrl: actionUrl.STORE_TYPE_PERMISSIONS_GET,
          },
        ],
      }),
      navItem("/gst", "GST", Landmark, {
        mandatoryPermissions: [
          { module: moduleName.GST, method: actionName.GET, actionUrl: actionUrl.GSTIN_LIST },
        ],
      }),
      navItem("/registers", "Registers", BookText, {
        mandatoryPermissions: [
          {
            module: moduleName.REGISTER,
            method: actionName.GET,
            actionUrl: actionUrl.REGISTER_LIST,
          },
        ],
      }),
      navItem("/discounts", "Discounts", Percent, {
        mandatoryPermissions: [
          {
            module: moduleName.DISCOUNT,
            method: actionName.GET,
            actionUrl: actionUrl.DISCOUNT_LIST,
          },
        ],
      }),
      navItem("/memberships", "Memberships", Award, {
        mandatoryPermissions: [
          {
            module: moduleName.MEMBERSHIP,
            method: actionName.GET,
            actionUrl: actionUrl.MEMBERSHIP_PLAN_LIST,
          },
        ],
      }),
      navItem("/loyalty", "Loyalty", Gift, {
        mandatoryPermissions: [
          {
            module: moduleName.LOYALTY,
            method: actionName.GET,
            actionUrl: actionUrl.LOYALTY_CONFIG,
          },
        ],
      }),
      navItem("/companies", "Transporters", Truck, {
        mandatoryPermissions: [
          { module: moduleName.COMPANY, method: actionName.GET, actionUrl: actionUrl.COMPANY_LIST },
        ],
      }),
      navItem("/expense-categories", "Expense Categories", Wallet, {
        mandatoryPermissions: [
          {
            module: moduleName.EXPENSE,
            method: actionName.GET,
            actionUrl: actionUrl.EXPENSE_CATEGORY_LIST,
          },
        ],
      }),
      navItem("/expenses", "Expenses", ReceiptText, {
        mandatoryPermissions: [
          {
            module: moduleName.EXPENSE,
            method: actionName.GET,
            actionUrl: actionUrl.EXPENSE_LIST,
          },
        ],
      }),
    ]),
    navGroup("Reports", BarChart3, [
      navItem("/reports/sales", "Sales", ReceiptText, {
        mandatoryPermissions: [
          { module: moduleName.REPORTS, method: actionName.GET, actionUrl: actionUrl.SALES_REPORT },
        ],
      }),
      navItem("/reports/stock-aging", "Stock Aging", BellRing, {
        mandatoryPermissions: [
          {
            module: moduleName.REPORTS,
            method: actionName.GET,
            actionUrl: actionUrl.STOCK_AGING_REPORT,
          },
        ],
      }),
      navItem("/reports/article-movement", "Article Movement", ArrowLeftRight, {
        mandatoryPermissions: [
          {
            module: moduleName.REPORTS,
            method: actionName.GET,
            actionUrl: actionUrl.ARTICLE_MOVEMENT,
          },
        ],
      }),
    ]),
    navGroup("Accounting", Calculator, [
      navItem("/accounting/trading-pl", "Trading & P&L", BarChart3, {
        mandatoryPermissions: [
          {
            module: moduleName.ACCOUNTING,
            method: actionName.GET,
            actionUrl: actionUrl.TRADING_PL_REPORT,
          },
        ],
      }),
      navItem("/accounting/purchase-sale", "Purchase & Sale", BarChart3, {
        mandatoryPermissions: [
          {
            module: moduleName.ACCOUNTING,
            method: actionName.GET,
            actionUrl: actionUrl.PURCHASE_SALE_REPORT,
          },
        ],
      }),
      navItem("/accounting/settings", "Thresholds", SlidersHorizontal, {
        mandatoryPermissions: [
          {
            module: moduleName.ACCOUNTING,
            method: actionName.GET,
            actionUrl: actionUrl.ACCOUNTING_CONFIG,
          },
        ],
      }),
    ]),
    navItem("/audit-log", "Audit log", ScrollText, {
      mandatoryPermissions: [
        {
          module: moduleName.AUDIT_LOG,
          method: actionName.GET,
          actionUrl: actionUrl.AUDIT_LOG_LIST,
        },
      ],
    }),
    navItem("/settings", "Settings", Settings, {
      optionalPermissions: [
        {
          module: moduleName.STORE_GST_SLABS,
          method: actionName.GET,
          actionUrl: actionUrl.STORE_GST_SLAB_LIST,
        },
      ],
    }),
  ].filter(Boolean);

export default function AppLayout({ children }) {
  const location = useLocation();
  const {
    user,
    permVersion,
    logout,
    assignedStores,
    activeStoreId,
    setActiveStoreId,
    loadPermissions,
  } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [openGroups, setOpenGroups] = useState({});

  // Recompute when user changes (login swaps permissions).
  const visibleNav = useMemo(() => NAV_DEFS(), [user, permVersion]);

  const activeStores = useMemo(
    () =>
      (Array.isArray(assignedStores) ? assignedStores : []).filter(
        (s) => !s.status || s.status === "ACTIVE",
      ),
    [assignedStores],
  );
  const showStoreSwitcher = activeStores.length > 1;

  const handleStoreSwitch = async (nextId) => {
    if (!Number.isFinite(nextId) || nextId === activeStoreId) return;
    setSwitching(true);
    try {
      await Request.postJson(authUrl.active_store, { storeId: nextId });
      Auth.activeStoreId = nextId;
      setActiveStoreId(nextId);
      // Re-bootstrap the permission set for the new store — the store-type
      // filter is applied per active store, so nav/button gating must
      // refresh on a switch (PRD story 19). Runs against the new
      // X-Store-Id (already set above) and bumps permVersion → sidebar
      // re-renders.
      await loadPermissions();
      // No token reissue — the axios interceptor injects the new
      // X-Store-Id on the next request. Force a remount so route data
      // refetches against the new store.
      navigate(location.pathname, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.message || "Could not switch store. Try again.";
      Notification.error(msg);
    } finally {
      setSwitching(false);
    }
  };

  // Searchable by store name and code (and city when present).
  const storeOptions = useMemo(
    () =>
      activeStores.map((s) => ({
        value: s.storeId,
        searchText: [s.storeName, s.storeCode, s.city].filter(Boolean).join(" ").toLowerCase(),
        label: (
          <div className="flex flex-col">
            <span className="text-cocoa font-medium">{s.storeName}</span>
            <span className="text-xs text-mahogany">
              {s.storeCode}
              {s.city ? ` · ${s.city}` : ""}
            </span>
          </div>
        ),
      })),
    [activeStores],
  );

  const titleMap = useMemo(() => {
    const map = {};
    for (const n of visibleNav) {
      if (n.children) {
        for (const c of n.children) map[c.to] = c.label;
      } else {
        map[n.to] = n.label;
      }
    }
    map["/profile"] = "Profile";
    return map;
  }, [visibleNav]);
  const title = titleMap[location.pathname] || "Sulsa";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navLinkClass = (isActive, highlight) =>
    [
      "flex items-center gap-3.5 rounded-xl px-4 py-3 text-[15px] font-medium transition-all duration-200",
      isActive
        ? "bg-[#6A4126] text-[#F7EFE0]"
        : "text-[#D8BA9C] hover:bg-[#6A4126]/40 hover:text-[#F7EFE0]",
      highlight && !isActive ? "border border-[#6A4126]" : "border border-transparent",
    ].join(" ");

  // Shared renderer for desktop + mobile sidebars. A group renders a toggle
  // header + indented child links; it defaults open when a child is active.
  const renderNavEntry = (item, onNavigate) => {
    const Icon = item.icon;
    if (item.children) {
      const childActive = item.children.some((c) => location.pathname === c.to);
      const open = openGroups[item.label] ?? childActive;
      return (
        <div key={item.label}>
          <button
            type="button"
            onClick={() => setOpenGroups((prev) => ({ ...prev, [item.label]: !open }))}
            className={`w-full ${navLinkClass(false, false)}`}
          >
            <Icon className="h-5 w-5 shrink-0" strokeWidth={1.5} />
            <span className="flex-1 text-left">{item.label}</span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
              strokeWidth={1.5}
            />
          </button>
          {open && (
            <div className="ml-5 mt-1 space-y-1 border-l border-[#5A3825] pl-2">
              {item.children.map((child) => {
                const ChildIcon = child.icon;
                const isActive = location.pathname === child.to;
                return (
                  <Link
                    key={child.to}
                    to={child.to}
                    onClick={onNavigate}
                    className={navLinkClass(isActive, false)}
                  >
                    <ChildIcon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                    <span>{child.label}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    const isActive = location.pathname === item.to;
    return (
      <Link
        key={item.to}
        to={item.to}
        onClick={onNavigate}
        className={navLinkClass(isActive, item.highlight)}
      >
        <Icon className="h-5 w-5 shrink-0" strokeWidth={1.5} />
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen flex bg-cream text-cocoa">
      {/* Sidebar - Desktop */}
      <aside className="w-[234px] shrink-0 bg-[#3D1A07] text-cream flex-col sticky top-0 h-screen hidden lg:flex">
        <div className="px-1 py-2 border-b border-[#5A3825] flex justify-center">
          <img
            src={sulsaLogo}
            alt="Sulsa — Woven with love"
            className="h-[75px] w-auto max-w-full object-contain"
          />
        </div>
        <nav className="flex-1 overflow-y-auto py-5 px-3 space-y-1.5 scrollbar-none">
          {visibleNav.map((item) => renderNavEntry(item))}
        </nav>
        <div className="p-5 flex items-center gap-3 mt-auto">
          <div
            onClick={() => navigate("/profile")}
            className="shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
          >
            <InitialsAvatar user={user} size={44} />
          </div>
          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate("/profile")}>
            <div className="text-[15px] font-medium text-[#F7EFE0] truncate">
              {user?.name || "Aarav Mehta"}
            </div>
            <div className="text-[13px] text-[#C4882A]">{user?.role || "Super Admin"}</div>
          </div>
          <Popconfirm
            title="Log Out"
            description="Are you sure you want to log out?"
            onConfirm={handleLogout}
            okText="Yes"
            cancelText="No"
            placement="topRight"
          >
            <button className="text-[#D8BA9C] hover:text-[#F7EFE0] transition-colors p-1">
              <LogOut className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </Popconfirm>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 bg-milk border-b border-border flex items-center px-4 lg:px-6 sticky top-0 z-10">
          <button
            className="lg:hidden mr-4 p-2 -ml-2 rounded-lg text-mahogany hover:bg-brown-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          <div>
            <div className="text-xs text-mahogany">Sulsa / {title}</div>
            <h1 className="font-display text-xl text-cocoa leading-none mt-1">{title}</h1>
          </div>

          <div className="ml-auto flex items-center gap-4">
            {showStoreSwitcher && (
              <AppSelect
                showSearch
                value={activeStoreId ?? undefined}
                placeholder="Select store"
                aria-label="Switch store"
                disabled={switching}
                loading={switching}
                options={storeOptions}
                onChange={handleStoreSwitch}
                width={220}
                optionLabelProp="label"
                filterOption={(input, option) =>
                  (option?.searchText || "").includes(input.toLowerCase())
                }
                prefix={<StoreIcon className="h-4 w-4 text-mahogany" strokeWidth={1.5} />}
              />
            )}

            <NotificationBell />

            <div
              onClick={() => navigate("/profile")}
              className="cursor-pointer hover:opacity-90 transition-opacity"
            >
              <InitialsAvatar user={user} size={36} />
            </div>
          </div>
        </header>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#3D1A07] text-[#F7EFE0] p-4 border-b border-[#5A3825] shadow-lg absolute top-[72px] left-0 right-0 z-20">
            <nav className="space-y-1.5">
              {visibleNav.map((item) => renderNavEntry(item, () => setMobileMenuOpen(false)))}
            </nav>
          </div>
        )}

        <main className="flex-1 p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}
